import { produce } from "immer";
/* 
This is a proof of concept for a file system operation via xapi to node.js
To be developed in parallel with the Delphi side of the application
*/
import {
  call,
  select,
  sleep,
  takeEvery,
  takeLeading,
  updateStore,
} from "starfx";

import { processSkipForwardPersistor } from "@app/statefx/selectors/applog.sel";

import { isErr, safeApi, safeLast } from "../helpers";
import { isItemOk, sysSeparator, transformData } from "../helpers/dirTree";
import { schema } from "../schema";
import { xApi, xThunk } from "../xapis";
import { applog } from "./app-log";

import type { TFSDirTreeItem } from "../helpers/dirTree";
import type { ApiCtx, Next, Operation } from "starfx";
function* parseableCtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx.json;
}

export const xapiCheckFileExists = xApi.get<string>(
  "/xapi/checkFileExists",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = false;
    ctx.request = ctx.req({
      url: `/routines/checkFileExists?fname=${ctx.payload}`,
    });
    yield* next();
  }
);

export const checkFileExists = function* (
  fileName: string
): Operation<boolean> {
  yield* call(xapiCheckFileExists.run(fileName));
  const fileExists = yield* safeApi(xapiCheckFileExists, fileName);
  if (isErr(fileExists)) {
    return false;
  } else {
    return true;
  }
};

function* identityACtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx;
}

function* commonTreeSliceSliceUpdate(
  payload: string,
  itemList: TFSDirTreeItem[]
) {
  const [, cif, invoice] = payload.split(sysSeparator);
  const fstree = yield* select(schema.fsDirTree.select);
  const newState = produce(fstree, (draft) => {
    if (!draft[cif]) {
      draft[cif] = {};
    }
    if (!draft[cif][invoice]) {
      draft[cif][invoice] = [];
    }
    draft[cif][invoice] = itemList;
    return draft;
  });
  yield* updateStore(schema.fsDirTree.set(newState));
}

export const xapiSpvReadFullTree = xApi.get<string>(
  "/xapi/spvReadFullTreeOfBase",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = false;
    //we will get our root. and build from here.. --> '.' is the root
    ctx.request = ctx.req({
      url: `/routines/spvReadFullTreeOfBase?base=${ctx.payload}`,
    });
    yield* next();
    const result = yield* safeLast(parseableCtx(ctx));
    if (isErr(result)) {
      console.log("error", result);
      return;
    }
    if (isItemOk(result)) {
      const itemList = result.value.items as TFSDirTreeItem[];
      if (ctx.payload === ".") {
        const data = transformData(itemList);
        yield* call(schema.update(schema.fsDirTree.set(data)));
      } else {
        yield* call(commonTreeSliceSliceUpdate(ctx.payload, itemList));
      }
    }
  }
);

export const xapiSpvReadShowVirtualTree = xApi.get<string>(
  "/xapi/spvReadShowVirtualTree",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = false;
    ctx.request = ctx.req({
      url: `/routines/spvReadShowVirtualTree?base=${ctx.payload}`,
    });
    yield* next();
  }
);

export const xapiSpvDownloadOriginalZip = xApi.get<string>(
  "/xapi/spvDownloadOriginalZip",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    //setting a persistor lock:
    yield* call(applog.run({ operation: "spv_msg_start", value: "" }));
    const payObj = JSON.parse(ctx.payload);
    const payload = new URLSearchParams(payObj).toString();
    ctx.cache = false;
    ctx.bodyType = "blob";
    ctx.request = ctx.req({
      url: `/routines/spvDownloadOriginalZip?${payload}`,
    });
    yield* next();

    const file = yield* safeLast(parseableCtx(ctx));
    // open the blob
    const blob = new Blob([file.value]);
    // create a download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Extract filename from Content-Disposition header
    const contentDisposition = ctx.response.headers.get("Content-Disposition");
    let fileName = "download.zip"; // Default filename

    if (contentDisposition && contentDisposition.includes("filename=")) {
      const matches = /filename="([^"]+)"/.exec(contentDisposition);
      if (matches != null && matches[1]) {
        fileName = matches[1];
      }
    }

    a.download = fileName; // Set the filename for the download link

    a.click();
    window.URL.revokeObjectURL(url);

    yield* sleep(300);
    // release the persistor lock:
    yield* call(applog.run({ operation: "spv_msg_end", value: "" }));
  }
);
export const lockForMessageStart = xThunk.create(
  "/lockForMessageStart",
  { supervisor: takeEvery },
  function* (ctx, next) {
    yield* call(applog.run({ operation: "spv_msg_start", value: "" }));
    yield* next();
  }
);

export const xapiSpvDownloadAsPdf = xApi.get<string>(
  "/xapi/spvDownloadAsPdf",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    // Setting a persistor lock:
    yield* call(applog.run({ operation: "spv_msg_start", value: "" }));

    console.log("persistor locked");

    const payObj = JSON.parse(ctx.payload);
    const payload = new URLSearchParams(payObj).toString();
    ctx.cache = false;
    ctx.bodyType = "blob";
    ctx.request = ctx.req({
      url: `/routines/spvDownloadAsPdf?${payload}`,
    });
    console.log("request set");

    yield* next();

    const file = yield* safeLast(parseableCtx(ctx));
    console.log("file received");

    // Open the blob
    const blob = new Blob([file.value], { type: "application/pdf" });
    console.log("blob created");
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    // Open the PDF in a new browser tab
    const newWindow = window.open(url);

    console.log("new window opened");

    if (!newWindow) {
      console.error("Failed to open new window/tab.");
    }

    // Release the blob URL after some time to avoid memory leaks
    console.log("sleeping");
    yield* sleep(300);
    console.log("waking up");
    window.URL.revokeObjectURL(url);
    console.log("blob url revoked");

    // Release the persistor lock:
    yield* call(applog.run({ operation: "spv_msg_end", value: "" }));
    console.log("persistor unlocked");
    yield* sleep(300);
    const shouldSkip = yield* select(processSkipForwardPersistor);
    console.log("shouldSkip", shouldSkip);
  }
);

export const nProcessSpvMessages = xApi.post<string>(
  "/routines/nProcessSpvMessages",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const execStack = yield* select(schema.execution.selectTableAsList);
    ctx.cache = false;
    ctx.request = ctx.req({
      url: `/routines/nProcessSpvMessages`,
      method: "POST",
      body: JSON.stringify({ data: execStack }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    yield* next();
  }
);

export const nDeleteServerFiles = xApi.delete<any>(
  "/routines/nDeleteServerFiles",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.cache = false;
    ctx.request = ctx.req({
      url: `/routines/nDeleteServerFiles`,
      method: "DELETE",
      body: JSON.stringify({ data: ctx.payload }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    yield* next();
  }
);
